import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetInvoicedHourlyRatesReportQuery } from 'store/api/reportingFinance';
import { InvoicedHourlyRatesRow } from 'types/reporting/finance';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';
import {
    ReportCaseLink,
    ReportContactLink,
    ReportEmployeeLink,
    ReportTeamLink,
    TONED_COLUMN_META,
    tonedCell,
    ToneRule,
    GREEN_OR_RED,
    YELLOW_OR_RED,
} from '../reportCells';
import {
    caseFilter,
    clientFilter,
    coreTeamFilter,
    employeeFilter,
    jobRoleFilter,
    periodFilter,
    periodQuickFilter,
    searchSelectors,
} from '../reportFilters';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';

type Row = InvoicedHourlyRatesRow;
type NumberKey = 'hourlyRate' | 'invoicedHours' | 'undiscountedAmount' | 'invoicedAmount';

const REPORT_KEY = 'invoiced-hourly-rates-by-employee-and-case';

function buildFilterConfig(): FilterConfig {
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'current-year' }),
        quickFilters: [],
        searchBarSelectors: searchSelectors('employeeName', 'clientName', 'caseNumber', 'caseName'),
        filters: [
            periodFilter(),
            employeeFilter('employeeId', 'reporting.filter.employee'),
            clientFilter(),
            caseFilter(),
            employeeFilter('supervisingPartnerId', 'reporting.filter.supervising-partner'),
            jobRoleFilter(),
            coreTeamFilter(),
        ],
    };
}

function numberColumn(
    rows: Row[] | undefined,
    id: NumberKey,
    labelId: string,
    rule: ToneRule
): DataTableColumnDef<Row, null> {
    return {
        id,
        accessorKey: id,
        header: t(labelId),
        meta: { ...TONED_COLUMN_META, widthWeight: 0.8, minPx: 130, textAlign: 'right', nullHandling: 'nullsLast' },
        cell: tonedCell(rows, (row) => row[id], rule),
    };
}

function buildColumns(rows: Row[] | undefined): DataTableColumnDef<Row, null>[] {
    return [
        {
            id: 'clientNumber',
            accessorKey: 'clientNumber',
            header: t('reporting.column.client-number'),
            meta: { widthWeight: 0.5, minPx: 90 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientNumber}</ReportContactLink>
            ),
        },
        {
            id: 'clientName',
            accessorKey: 'clientName',
            header: t('reporting.column.client-name'),
            meta: { widthWeight: 1.2, minPx: 160 },
            cell: ({ row }) => (
                <ReportContactLink partyId={row.original.clientId}>{row.original.clientName}</ReportContactLink>
            ),
        },
        {
            id: 'caseNumber',
            accessorKey: 'caseNumber',
            header: t('reporting.column.case-number'),
            meta: { widthWeight: 0.6, minPx: 110 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId} wrapLabel>
                    {row.original.caseNumber}
                </ReportCaseLink>
            ),
        },
        {
            id: 'caseName',
            accessorKey: 'caseName',
            header: t('reporting.column.case-name'),
            meta: { widthWeight: 1.4, minPx: 180 },
            cell: ({ row }) => (
                <ReportCaseLink matterId={row.original.matterId}>{row.original.caseName}</ReportCaseLink>
            ),
        },
        {
            id: 'employeeName',
            accessorKey: 'employeeName',
            header: t('reporting.column.employee'),
            meta: { widthWeight: 1, minPx: 150 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.employeeId}>
                    {row.original.employeeName}
                </ReportEmployeeLink>
            ),
        },
        {
            id: 'jobRole',
            accessorKey: 'jobRole',
            header: t('reporting.column.job-role'),
            meta: { widthWeight: 0.8, minPx: 120 },
        },
        {
            id: 'coreTeam',
            accessorKey: 'coreTeam',
            header: t('reporting.column.core-team'),
            meta: { widthWeight: 0.8, minPx: 120 },
            cell: ({ row }) => (
                <ReportTeamLink teamId={row.original.coreTeamId}>{row.original.coreTeam}</ReportTeamLink>
            ),
        },
        {
            id: 'accountingPeriod',
            accessorKey: 'accountingPeriod',
            header: t('reporting.column.accounting-period'),
            meta: { fixedPx: 110 },
        },
        numberColumn(rows, 'hourlyRate', 'reporting.column.hourly-rate', GREEN_OR_RED),
        numberColumn(rows, 'invoicedHours', 'reporting.column.invoiced-hours', GREEN_OR_RED),
        numberColumn(rows, 'undiscountedAmount', 'reporting.column.undiscounted-amount', YELLOW_OR_RED),
        numberColumn(rows, 'invoicedAmount', 'reporting.column.invoiced-amount', GREEN_OR_RED),
        {
            id: 'currency',
            accessorKey: 'currency',
            header: t('reporting.column.case-currency'),
            meta: { fixedPx: 130 },
        },
    ];
}

export function InvoicedHourlyRatesReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetInvoicedHourlyRatesReportQuery();
    const filterConfig = useMemo(() => buildFilterConfig(), []);
    const columns = useMemo(() => buildColumns(data?.content), [data]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)}>
            <ListTableWithFilters<Row, ReportPage<Row, null>>
                data={data}
                belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                isLoading={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                columns={columns}
                columnConfigId={`report-${REPORT_KEY}`}
                enableColumnPicker
                enableColumnReorder
                enableColumnResize
                pluralTextId="reporting.rows"
                singularTextId="reporting.row"
                noEntriesId="reporting.no-rows"
                noEntriesBodyId="reporting.no-rows-body"
            />
        </ReportLayout>
    );
}
